import { useMemo } from 'react';
import { Prompt } from '../types';

export const useStats = (prompts: Prompt[]) => {
  return useMemo(() => {
    const totalPrompts = prompts.length;
    const favoritePrompts = prompts.filter(p => p.isFavorite).length;
    const templatePrompts = prompts.filter(p => p.isTemplate).length;
    
    // Calculate most used platform
    const platformCounts: Record<string, number> = {};
    prompts.forEach(prompt => {
      if (prompt.aiPlatform) {
        platformCounts[prompt.aiPlatform] = (platformCounts[prompt.aiPlatform] || 0) + 1;
      }
    });
    
    const mostUsedPlatform = Object.entries(platformCounts)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || '';
    
    // Calculate recent activity (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recentActivity = prompts.filter(p => p.createdAt >= sevenDaysAgo).length;
    
    // Calculate creation streak (simplified - days with at least one prompt)
    const datesWithActivity = new Set<string>();
    prompts.forEach(prompt => {
      const dateStr = prompt.createdAt.toDateString();
      datesWithActivity.add(dateStr);
    });
    
    // Calculate consecutive days with activity (simplified)
    let creationStreak = 0;
    const today = new Date();
    for (let i = 0; i < 30; i++) { // Check last 30 days
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() - i);
      const dateStr = checkDate.toDateString();
      
      if (datesWithActivity.has(dateStr)) {
        creationStreak++;
      } else if (i > 0) { // Don't break on first day if no activity today
        break;
      }
    }
    
    // Platform distribution for charts
    const platformDistribution = Object.entries(platformCounts)
      .map(([platform, count]) => ({
        platform,
        count,
        percentage: Math.round((count / totalPrompts) * 100)
      }))
      .sort((a, b) => b.count - a.count);
    
    // Activity timeline data
    const activityTimeline: { date: string; count: number }[] = [];
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date;
    }).reverse();
    
    last30Days.forEach(date => {
      const dateStr = date.toISOString().split('T')[0];
      const count = prompts.filter(p => 
        p.createdAt.toISOString().split('T')[0] === dateStr
      ).length;
      
      activityTimeline.push({
        date: dateStr,
        count
      });
    });
    
    return {
      totalPrompts,
      favoritePrompts,
      templatePrompts,
      mostUsedPlatform,
      recentActivity,
      totalTags: 0, // No tags anymore
      creationStreak,
      platformDistribution,
      activityTimeline,
    };
  }, [prompts]);
};